import React, { useCallback, useEffect, useState } from "react";
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    Switch,
    Space,
    message,
    Popconfirm,
    Avatar,
    Typography,
    Tabs,
    Tag,
    Progress,
} from "antd";
import {
    PlusOutlined,
    GiftOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import { api } from "../api";
import { fmtDate } from "../utils";
import ImageUpload from "../components/ImageUpload";

const { Text } = Typography;

/* --------------------------- Gift catalog tab --------------------------- */
const GiftsTab = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.listGifts();
            setItems(res.items);
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const openEdit = (r) => {
        setEditing(r);
        form.setFieldsValue({ active: true, ...r });
    };
    const openCreate = () => {
        setEditing({});
        form.resetFields();
        form.setFieldsValue({ active: true, cost: 1, stock: 1 });
    };

    const save = async () => {
        const v = await form.validateFields();
        if (!v.image) {
            message.error("Please upload an image for the gift");
            return;
        }
        setSaving(true);
        try {
            if (editing._id) {
                await api.updateGift(editing._id, v);
                message.success("Updated");
            } else {
                await api.createGift(v);
                message.success("Added");
            }
            setEditing(null);
            load();
        } catch (e) {
            message.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            await api.deleteGift(id);
            message.success("Deleted");
            load();
        } catch (e) {
            message.error(e.message);
        }
    };

    const columns = [
        {
            title: "Image",
            key: "image",
            width: 80,
            render: (_, r) => (
                <Avatar shape="square" size={52} src={r.image} icon={<GiftOutlined />} />
            ),
        },
        { title: "Title", dataIndex: "title", key: "title" },
        {
            title: "Cost",
            dataIndex: "cost",
            key: "cost",
            width: 90,
            render: (v) => <Tag color="gold">{v} coins</Tag>,
        },
        {
            title: "Claimed / Stock",
            key: "stock",
            width: 180,
            render: (_, r) => (
                <Space direction="vertical" size={2} style={{ width: 140 }}>
                    <Text>
                        {r.claimedCount} / {r.stock}
                    </Text>
                    <Progress
                        percent={r.stock ? Math.round((r.claimedCount / r.stock) * 100) : 0}
                        size="small"
                        showInfo={false}
                    />
                </Space>
            ),
        },
        {
            title: "Active",
            dataIndex: "active",
            key: "active",
            width: 90,
            render: (v) => (v ? <Tag color="green">Active</Tag> : <Tag>Hidden</Tag>),
        },
        {
            title: "Actions",
            key: "actions",
            width: 160,
            render: (_, r) => (
                <Space>
                    <Button size="small" onClick={() => openEdit(r)}>
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete this gift and its claims?"
                        onConfirm={() => remove(r._id)}
                    >
                        <Button size="small" danger>
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="admin-page-head" style={{ marginBottom: 8 }}>
                <Text type="secondary">
                    Gifts volunteers can redeem with CTRL coins. Stock is the global
                    number of units; a gift sells out once claimed reaches stock.
                </Text>
                <Space>
                    <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
                        Refresh
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        Add gift
                    </Button>
                </Space>
            </div>

            <Table
                rowKey="_id"
                loading={loading}
                columns={columns}
                dataSource={items}
                scroll={{ x: "max-content" }}
                style={{ marginTop: 8 }}
            />

            <Modal
                open={!!editing}
                title={editing && editing._id ? "Edit gift" : "Add gift"}
                onCancel={() => setEditing(null)}
                onOk={save}
                confirmLoading={saving}
                width={540}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="title"
                        label="Title"
                        rules={[{ required: true, message: "A title is required" }]}
                    >
                        <Input placeholder="e.g. CTRL brand hoodie" />
                    </Form.Item>
                    <Form.Item
                        name="image"
                        label="Image"
                        rules={[{ required: true, message: "An image is required" }]}
                    >
                        <ImageUpload />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input.TextArea rows={3} placeholder="Short description of the gift…" />
                    </Form.Item>
                    <Form.Item
                        name="sponsorName"
                        label="Provided by (company)"
                        tooltip="Who gives this gift. Leave as CTRL if it's from us."
                        initialValue="CTRL"
                    >
                        <Input placeholder="e.g. CTRL, or a partner company name" />
                    </Form.Item>
                    <Form.Item
                        name="sponsorLogo"
                        label="Company logo (optional)"
                        tooltip="Shown next to the company name on the gift page"
                    >
                        <ImageUpload />
                    </Form.Item>
                    <Space size="large" style={{ display: "flex" }}>
                        <Form.Item
                            name="cost"
                            label="Cost (coins)"
                            rules={[{ required: true, message: "Required" }]}
                        >
                            <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item
                            name="stock"
                            label="Stock (total units)"
                            rules={[{ required: true, message: "Required" }]}
                        >
                            <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                        <Form.Item name="active" label="Active" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Space>
                </Form>
            </Modal>
        </div>
    );
};

/* ----------------------------- Claims tab ------------------------------ */
const ClaimsTab = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.listGiftClaims();
            setItems(res.items);
        } catch (e) {
            message.error(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const deliver = async (id) => {
        setBusyId(id);
        try {
            await api.deliverGiftClaim(id);
            message.success("Marked delivered");
            load();
        } catch (e) {
            message.error(e.message);
        } finally {
            setBusyId(null);
        }
    };
    const undeliver = async (id) => {
        setBusyId(id);
        try {
            await api.undeliverGiftClaim(id);
            message.success("Reverted to claimed");
            load();
        } catch (e) {
            message.error(e.message);
        } finally {
            setBusyId(null);
        }
    };

    const columns = [
        {
            title: "Gift",
            key: "gift",
            render: (_, r) => (
                <Space>
                    <Avatar shape="square" size={40} src={r.gift?.image} icon={<GiftOutlined />} />
                    <span>{r.gift?.title || "—"}</span>
                </Space>
            ),
        },
        {
            title: "Volunteer",
            key: "user",
            render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{r.user?.name || "—"}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {r.user?.email || r.user?.phone || ""}
                    </Text>
                </Space>
            ),
        },
        {
            title: "Paid",
            dataIndex: "costPaid",
            key: "costPaid",
            width: 90,
            render: (v) => <Tag color="gold">{v} coins</Tag>,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (v) =>
                v === "DELIVERED" ? (
                    <Tag color="green">Delivered</Tag>
                ) : (
                    <Tag color="orange">Claimed</Tag>
                ),
        },
        {
            title: "Claimed on",
            dataIndex: "claimedAt",
            key: "claimedAt",
            width: 160,
            render: (d) => fmtDate(d, true),
        },
        {
            title: "Delivered on",
            dataIndex: "deliveredAt",
            key: "deliveredAt",
            width: 160,
            render: (d) => (d ? fmtDate(d, true) : <Text type="secondary">—</Text>),
        },
        {
            title: "Actions",
            key: "actions",
            width: 160,
            render: (_, r) =>
                r.status === "DELIVERED" ? (
                    <Button size="small" loading={busyId === r._id} onClick={() => undeliver(r._id)}>
                        Undo
                    </Button>
                ) : (
                    <Button
                        size="small"
                        type="primary"
                        loading={busyId === r._id}
                        onClick={() => deliver(r._id)}
                    >
                        Mark delivered
                    </Button>
                ),
        },
    ];

    return (
        <div>
            <div className="admin-page-head" style={{ marginBottom: 8 }}>
                <Text type="secondary">
                    Every gift a volunteer has taken. Mark it delivered once you hand
                    over the physical item.
                </Text>
                <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
                    Refresh
                </Button>
            </div>
            <Table
                rowKey="_id"
                loading={loading}
                columns={columns}
                dataSource={items}
                scroll={{ x: "max-content" }}
                style={{ marginTop: 8 }}
            />
        </div>
    );
};

const GiftsPage = () => {
    return (
        <div>
            <div className="admin-page-head">
                <h1 className="admin-page-title">Gifts</h1>
            </div>
            <Tabs
                defaultActiveKey="gifts"
                items={[
                    { key: "gifts", label: "Gifts", children: <GiftsTab /> },
                    { key: "claims", label: "Claims", children: <ClaimsTab /> },
                ]}
            />
        </div>
    );
};

export default GiftsPage;
